import { useEexCapability } from "../components/eex/use-eex-capability";
import "../features/eex/operations-pages.css";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  EexMarginLabBootstrapResponseSchema,
  EexMarginLabCalculationResponseSchema,
  type EexMarginLabCandidatePosition,
  type EexMarginLabCalculationResponse,
  type EexMarginLabRiskSnapshot,
} from "@chro/shared";
import { apiContract } from "../lib/api";
import { cn } from "../lib/utils";
import { Td, Th } from "../components/eex/ui";

export const Route = createFileRoute("/eex/margin-lab")({
  component: EexMarginLabPage,
});

interface SimulatedPosition {
  id: string;
  product: string;
  period: string;
  signedQty: number;
  book: string | null;
  trader: string;
}

interface PositionLike {
  id: string;
  product: string;
  period: string;
  signedQty: number;
  book: string | null;
  trader: string;
}

const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const money = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// This is intentionally the trader-facing vocabulary, rather than ECC's
// short codes. `resolveMarginLabFutureContract` maps these names and the
// selected delivery period to the exact ECC contract code on the server.
const MARGIN_LAB_INSTRUMENT_GROUPS = [
  {
    label: "Power",
    instruments: [
      "Germany Baseload", "Germany Peakload", "Hungary Baseload", "Hungary Peakload",
      "Hungarian Baseload", "Hungarian Peakload", "France Baseload", "France Peakload",
      "Romania Baseload", "Romania Peakload", "Bulgaria Baseload", "Italy Baseload",
      "Italy Peakload", "Greece Baseload", "Czech Baseload", "Czech Peakload",
      "Serbia Baseload", "Slovenia Baseload", "Slovenia Peakload", "Slovakia Baseload",
      "Slovakia Peakload", "Croatia Baseload", "Croatia Peakload", "Austria Baseload",
      "Austria Peakload", "Great Britain Baseload", "Great Britain Peakload", "Ireland SEM Baseload",
      "Finland Baseload", "Nordic System Baseload", "Sweden SE1 Baseload", "Sweden SE2 Baseload",
      "Sweden SE3 Baseload", "Sweden SE4 Baseload", "Norway NO1 Baseload", "Norway NO2 Baseload",
      "Norway NO3 Baseload", "Norway NO4 Baseload", "Norway NO5 Baseload", "Denmark DK1 Baseload",
      "Denmark DK2 Baseload", "Spain Baseload", "Spain Peakload", "Netherlands Baseload",
      "Netherlands Peakload", "Belgium Baseload", "Switzerland Baseload", "Switzerland Peakload",
      "Poland Baseload", "Poland Peakload",
    ],
  },
  {
    label: "Natural gas",
    instruments: [
      "THE EGSI Natural Gas", "THE Natural Gas", "PSV Natural Gas", "PEG Natural Gas",
      "NBP EGSI Natural Gas", "NBP Natural Gas", "ZTP Natural Gas", "Czech VTP Natural Gas",
      "CEGH VTP EGSI Natural Gas", "CEGH VTP Natural Gas", "PVB Natural Gas",
      "TTF EGSI Natural Gas", "TTF Natural Gas",
    ],
  },
] as const;

function EexMarginLabPage() {
  const labQ = useQuery({
    queryKey: ["eex-margin-lab"],
    queryFn: () => apiContract(EexMarginLabBootstrapResponseSchema, "/eex/margin-lab"),
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [simulated, setSimulated] = useState<SimulatedPosition[]>([]);
  const [search, setSearch] = useState("");
  const canCalculate = useEexCapability("margin_lab.calculate");
  const [calculatedScenarioKey, setCalculatedScenarioKey] = useState<string | null>(null);
  const initializedData = useRef<unknown>(null);
  const calculateM = useMutation({
    mutationFn: (input: {
      riskSnapshotId: string;
      selectedPositionIds: string[];
      simulatedPositions: Array<{
        product: string;
        period: string;
        signedQty: number;
        book: string | null;
        trader: string;
      }>;
    }) => apiContract(
      EexMarginLabCalculationResponseSchema,
      "/eex/margin-lab/calculate",
      { method: "POST", body: JSON.stringify(input) }
    ),
  });

  const data = labQ.data;
  const eligiblePositions = useMemo(
    () => (data?.positions ?? []).filter((position) => position.eligible),
    [data?.positions]
  );

  // Whole cleared portfolio is the useful starting point. This runs once for a
  // fetched dataset; thereafter the user has full control over the picks.
  useEffect(() => {
    if (!data || initializedData.current === data) return;
    initializedData.current = data;
    setSelectedIds(new Set(data.positions.filter((position) => position.eligible).map((position) => position.id)));
    setSelectedSnapshotId(data.defaultRiskSnapshotId);
  }, [data]);

  const selectedLive = useMemo(
    () => eligiblePositions.filter((position) => selectedIds.has(position.id)),
    [eligiblePositions, selectedIds]
  );
  const selectedLiveForSummary: PositionLike[] = useMemo(
    () => selectedLive.map((position) => ({
      id: position.id,
      product: position.product,
      period: position.period,
      signedQty: position.signedQty,
      book: position.book,
      trader: position.trader.label,
    })),
    [selectedLive]
  );
  const selectedSnapshot = data?.riskSnapshots.find((snapshot) => snapshot.id === selectedSnapshotId) ?? null;
  const allPositions: PositionLike[] = useMemo(
    () => [
      ...selectedLiveForSummary,
      ...simulated,
    ],
    [selectedLiveForSummary, simulated]
  );
  const totalNetMw = allPositions.reduce((sum, position) => sum + position.signedQty, 0);
  const totalGrossMw = allPositions.reduce((sum, position) => sum + Math.abs(position.signedQty), 0);
  const selectedByBook = useMemo(() => summarizeBy(allPositions, (position) => position.book ?? "Unbooked"), [allPositions]);
  const selectedByTrader = useMemo(() => summarizeBy(allPositions, (position) => position.trader), [allPositions]);
  const scenarioKey = useMemo(
    () => JSON.stringify({
      riskSnapshotId: selectedSnapshotId,
      selectedPositionIds: [...selectedIds].sort(),
      simulated: simulated.map(({ id, product, period, signedQty }) => ({ id, product, period, signedQty })),
    }),
    [selectedIds, selectedSnapshotId, simulated]
  );
  const calculation = calculatedScenarioKey === scenarioKey ? calculateM.data : null;
  const filteredPositions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return data?.positions ?? [];
    return (data?.positions ?? []).filter((position) =>
      [position.tradeId, position.product, position.period, position.book ?? "", position.trader.label]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [data?.positions, search]);

  const setAllEligible = (): void => setSelectedIds(new Set(eligiblePositions.map((position) => position.id)));
  const clearSelection = (): void => setSelectedIds(new Set());
  const togglePosition = (id: string): void => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleGroup = (positions: EexMarginLabCandidatePosition[]): void => {
    const ids = positions.filter((position) => position.eligible).map((position) => position.id);
    if (ids.length === 0) return;
    setSelectedIds((previous) => {
      const allSelected = ids.every((id) => previous.has(id));
      const next = new Set(previous);
      for (const id of ids) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };
  const calculate = (): void => {
    if (!selectedSnapshotId) return;
    setCalculatedScenarioKey(null);
    calculateM.mutate(
      {
        riskSnapshotId: selectedSnapshotId,
        selectedPositionIds: [...selectedIds],
        simulatedPositions: simulated.map(({ product, period, signedQty, book, trader }) => ({
          product,
          period,
          signedQty,
          book,
          trader,
        })),
      },
      { onSuccess: () => setCalculatedScenarioKey(scenarioKey) }
    );
  };

  return (
    <div className="operations-workspace ops-page ops-margin-lab min-w-0 space-y-4 px-3 py-4 sm:space-y-6 sm:p-4">
      <header className="ops-heading min-w-0 space-y-1">
        <h1 className="font-semibold text-lg">EEX Margin Lab</h1>
        <p className="max-w-3xl text-xs text-muted">
          Compose a portfolio from cleared trade contributions and hypothetical positions, then pin it to an ECC risk date.
          This working set never changes your saved trades or clearing records.
        </p>
      </header>

      {labQ.isLoading && <div className="text-sm text-muted">Loading portfolio inputs…</div>}
      {labQ.isError && <div className="text-sm text-negRed">{(labQ.error as Error).message}</div>}

      {data && (
        <>
          <RiskSnapshotRail
            snapshots={data.riskSnapshots}
            selectedId={selectedSnapshotId}
            onSelect={setSelectedSnapshotId}
          />

          <section className="ops-panel ops-lab-section space-y-3 rounded-md border border-border/60 p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="ops-section-title font-semibold text-sm">Portfolio collection</h2>
                <p className="text-xs text-muted">
                  Start with all cleared trade contributions, then narrow it by Book, trader, or individual selection.
                  The calculator resolves the selected contributions into contract positions before applying ECC's scenario arrays.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={setAllEligible} className="rounded border border-border px-2 py-1 text-xs hover:border-accent">
                  All cleared contributions
                </button>
                <button type="button" onClick={clearSelection} className="rounded border border-border px-2 py-1 text-xs text-muted hover:border-accent">
                  Clear live picks
                </button>
              </div>
            </div>

            <ScopeChips
              label="Books"
              groups={groupCandidates(eligiblePositions, (position) => position.book ?? "Unbooked")}
              selectedIds={selectedIds}
              onToggle={toggleGroup}
            />
            <ScopeChips
              label="Traders"
              groups={groupCandidates(eligiblePositions, (position) => position.trader.label)}
              selectedIds={selectedIds}
              onToggle={toggleGroup}
            />

            <label className="block max-w-md text-xs">
              <span className="mb-1 block font-medium text-fg">Find individual positions</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Trade ID, product, period, Book or trader"
                className="w-full rounded border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:border-accent"
              />
            </label>
            <PositionPicker positions={filteredPositions} selectedIds={selectedIds} onToggle={togglePosition} />
          </section>

          <SimulatedPositionEditor positions={simulated} onChange={setSimulated} />

          <section className="ops-panel ops-lab-section space-y-3 rounded-md border border-border/60 p-3">
            <div>
              <h2 className="ops-section-title font-semibold text-sm">Prepared scenario</h2>
              <p className="text-xs text-muted">The PC-SPAN calculator resolves this collection into contract positions before applying scenario risk.</p>
            </div>
            <div className="ops-lab-metrics grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
              <Metric label="Selected trade contributions" value={number.format(selectedLive.length)} />
              <Metric label="Simulated positions" value={number.format(simulated.length)} />
              <Metric label="Net / gross lots" value={`${signed(totalNetMw)} / ${number.format(totalGrossMw)}`} />
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <ScopeSummary title="Scenario by Book" rows={selectedByBook} />
              <ScopeSummary title="Scenario by trader" rows={selectedByTrader} />
            </div>
          </section>

          <section className="ops-lab-section ops-lab-calculation space-y-3 rounded-md border border-accent/40 bg-accent/5 p-3 text-xs">
            <div>
              <h2 className="ops-section-title font-semibold text-sm text-fg">PC-SPAN futures scan risk</h2>
              <p className="mt-1 max-w-3xl text-muted">{data.estimator.reason}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={calculate}
                disabled={
                  !canCalculate || !selectedSnapshot ||
                  selectedSnapshot.availability !== "risk_available" ||
                  allPositions.length === 0 ||
                  calculateM.isPending
                }
                className="ops-primary rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {calculateM.isPending ? "Calculating…" : "Calculate dated scan risk"}
              </button>
              <span className="text-muted">
                {selectedSnapshot
                  ? `${selectedSnapshot.businessDate}: ${selectedSnapshot.availabilityReason}`
                  : "Choose an ECC archive date before calculating."}
              </span>
            </div>
            {calculateM.isError && (
              <div className="text-negRed">{(calculateM.error as Error).message}</div>
            )}
            {calculation && <ScanRiskResult calculation={calculation} />}
            <p className="max-w-3xl text-muted">
              This is not presented as a final ECC initial-margin requirement: spread credits/charges, delivery and concentration add-ons,
              margin-account allocation, and CC750 reconciliation remain separate.
            </p>
            <p className="max-w-3xl text-muted">{data.marginAccountStatus.reason}</p>
            <Link to="/eex/margin" search={{ analysis: "vm" }} className="inline-block text-accent underline underline-offset-2 sm:no-underline sm:hover:underline">
              View the existing reconciled variation-margin ledger →
            </Link>
          </section>
        </>
      )}
    </div>
  );
}

function ScanRiskResult({ calculation }: { calculation: EexMarginLabCalculationResponse }) {
  if (calculation.status === "unavailable") {
    return (
      <div className="space-y-2 rounded border border-warnOrange/40 bg-warnOrange/5 p-2 text-xs">
        <div className="font-medium text-fg">No scan-risk number for this collection</div>
        <p className="text-muted">{calculation.completenessReason}</p>
        {calculation.exclusions.length > 0 && (
          <ul className="list-disc space-y-1 pl-4 text-muted">
            {calculation.exclusions.map((exclusion) => (
              <li key={`${exclusion.productCode}-${exclusion.periodCode}`}>
                {exclusion.productCode} {exclusion.periodCode} ({signed(exclusion.signedLots)} lots): {exclusion.reason}
              </li>
            ))}
          </ul>
        )}
        <ScopeScanRiskBreakdowns calculation={calculation} />
      </div>
    );
  }

  const displayAmount = calculation.scanRisk == null || calculation.currency == null
    ? "Unavailable"
    : `${calculation.currency} ${money.format(calculation.scanRisk)}`;
  return (
    <div className="space-y-2 rounded border border-border/60 bg-bg/40 p-2 text-xs">
      <div className="grid gap-2 sm:grid-cols-2">
        <Metric label="Futures PC-SPAN scan risk" value={displayAmount} />
        <Metric label="ECC archive date" value={calculation.businessDate} />
      </div>
      <p className="text-muted">{calculation.completenessReason}</p>
      <div className="ops-table-frame overflow-x-auto rounded border border-border/40">
        <table className="w-full text-xs">
          <thead className="bg-bg text-left text-muted">
            <tr>
              <Th>Combined commodity</Th>
              <Th className="text-right">Worst scenario</Th>
              <Th className="text-right">Scan risk</Th>
            </tr>
          </thead>
          <tbody>
            {calculation.combinedCommodities.map((combined) => (
              <tr key={combined.productCode} className="border-t border-border/40">
                <Td>{combined.productCode}</Td>
                <Td className="text-right tabular-nums">{combined.worstScenario}</Td>
                <Td className="text-right tabular-nums">{combined.currency} {money.format(combined.scanRisk)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ScopeScanRiskBreakdowns calculation={calculation} />
    </div>
  );
}

function ScopeScanRiskBreakdowns({ calculation }: { calculation: EexMarginLabCalculationResponse }) {
  return (
    <div className="grid gap-2 lg:grid-cols-2">
      <ScopeScanRiskSummary title="Standalone scan risk by Book" rows={calculation.breakdowns.byBook} />
      <ScopeScanRiskSummary title="Standalone scan risk by trader" rows={calculation.breakdowns.byTrader} />
    </div>
  );
}

function ScopeScanRiskSummary({
  title,
  rows,
}: {
  title: string;
  rows: EexMarginLabCalculationResponse["breakdowns"]["byBook"];
}) {
  return (
    <div className="rounded border border-border/40 p-2">
      <div className="font-medium text-fg">{title}</div>
      <p className="mt-0.5 text-muted">Independent views, not allocated shares of the portfolio result.</p>
      <div className="mt-1 space-y-1">
        {rows.map((row) => {
          const amount = row.status === "calculated" && row.currency != null && row.scanRisk != null
            ? `${row.currency} ${money.format(row.scanRisk)}`
            : "Unavailable";
          return (
            <div key={row.label} className="flex items-start justify-between gap-3">
              <span className="min-w-0 truncate" title={row.completenessReason}>{row.label}</span>
              <span className={cn("shrink-0 tabular-nums", row.status === "unavailable" && "text-warnOrange")}>{amount}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RiskSnapshotRail({
  snapshots,
  selectedId,
  onSelect,
}: {
  snapshots: EexMarginLabRiskSnapshot[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <section className="ops-panel ops-lab-section space-y-2 rounded-md border border-border/60 p-3">
      <div>
        <h2 className="ops-section-title font-semibold text-sm">ECC risk snapshot</h2>
        <p className="text-xs text-muted">Choose the dated ECC archive against which this collection will be evaluated.</p>
      </div>
      {snapshots.length === 0 ? (
        <div className="text-xs text-muted">No ECC SPAN archives have been retained yet.</div>
      ) : (
        <div className="ops-lab-snapshots flex gap-2 overflow-x-auto pb-1" aria-label="ECC risk snapshot dates">
          {snapshots.map((snapshot) => {
            const active = snapshot.id === selectedId;
            return (
              <button
                type="button"
                key={snapshot.id}
                onClick={() => onSelect(snapshot.id)}
                aria-pressed={active}
                title={snapshot.availabilityReason}
                className={cn(
                  "min-w-28 shrink-0 rounded border px-2 py-2 text-left text-xs transition-colors",
                  active ? "border-accent bg-accent/10" : "border-border hover:border-accent"
                )}
              >
                <span className="block font-mono font-semibold">{snapshot.businessDate}</span>
                <span className={cn(
                  "mt-1 block",
                  snapshot.availability === "import_failed" ? "text-negRed" : "text-muted"
                )}>
                  {snapshot.availability === "risk_available" ? "risk arrays available" : snapshot.availability.replace("_", " ")}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ScopeChips({
  label,
  groups,
  selectedIds,
  onToggle,
}: {
  label: string;
  groups: Array<{ label: string; positions: EexMarginLabCandidatePosition[] }>;
  selectedIds: ReadonlySet<string>;
  onToggle: (positions: EexMarginLabCandidatePosition[]) => void;
}) {
  if (groups.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {groups.map((group) => {
          const selected = group.positions.every((position) => selectedIds.has(position.id));
          return (
            <button
              type="button"
              key={group.label}
              onClick={() => onToggle(group.positions)}
              aria-pressed={selected}
              className={cn(
                "rounded-full border px-2 py-1 text-xs",
                selected ? "border-accent bg-accent/10 text-fg" : "border-border text-muted hover:border-accent"
              )}
            >
              {group.label} · {group.positions.length}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PositionPicker({
  positions,
  selectedIds,
  onToggle,
}: {
  positions: EexMarginLabCandidatePosition[];
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="ops-table-frame max-h-96 overflow-auto rounded border border-border/60">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-bg text-left text-muted">
          <tr>
            <Th className="w-10">Use</Th>
            <Th>Trade contribution</Th>
            <Th>Book</Th>
            <Th>Trader</Th>
            <Th className="text-right">MW</Th>
          </tr>
        </thead>
        <tbody>
          {positions.map((position) => (
            <tr key={position.id} className="border-t border-border/40">
              <Td>
                <input
                  type="checkbox"
                  checked={selectedIds.has(position.id)}
                  disabled={!position.eligible}
                  onChange={() => onToggle(position.id)}
                  aria-label={`Use ${position.tradeId} ${position.product} ${position.period}`}
                />
              </Td>
              <Td>
                <div className="font-medium text-fg">{position.product} · {position.period}</div>
                <div className="font-mono text-muted">{position.tradeId}</div>
                {!position.eligible && <div className="text-warnOrange">{position.exclusionReason}</div>}
              </Td>
              <Td>{position.book ?? <span className="text-muted">Unbooked</span>}</Td>
              <Td>{position.trader.label}</Td>
              <Td className={cn("text-right tabular-nums", position.signedQty < 0 && "text-negRed")}>
                {signed(position.signedQty)}
              </Td>
            </tr>
          ))}
          {positions.length === 0 && (
            <tr><Td colSpan={5} className="py-4 text-center text-muted">No positions match this filter.</Td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SimulatedPositionEditor({
  positions,
  onChange,
}: {
  positions: SimulatedPosition[];
  onChange: (positions: SimulatedPosition[]) => void;
}) {
  const [instrument, setInstrument] = useState("");
  const [period, setPeriod] = useState("");
  const [side, setSide] = useState<"B" | "S">("B");
  const [quantity, setQuantity] = useState("1");
  const [book, setBook] = useState("");
  const [trader, setTrader] = useState("Simulated");
  const [error, setError] = useState<string | null>(null);

  const add = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const lots = Number(quantity);
    if (!instrument || !period.trim() || !Number.isInteger(lots) || lots <= 0) {
      setError("Instrument, period and a whole positive lot quantity are required.");
      return;
    }
    setError(null);
    onChange([
      ...positions,
      {
        id: globalThis.crypto?.randomUUID?.() ?? `sim-${Date.now()}-${positions.length}`,
        product: instrument,
        period: period.trim(),
        signedQty: side === "B" ? lots : -lots,
        book: book.trim() || null,
        trader: trader.trim() || "Simulated",
      },
    ]);
    setInstrument("");
    setPeriod("");
    setQuantity("1");
  };

  return (
    <section className="ops-panel ops-lab-section space-y-3 rounded-md border border-border/60 p-3">
      <div>
        <h2 className="ops-section-title font-semibold text-sm">Simulated positions</h2>
        <p className="text-xs text-muted">Add hypothetical lots to include them in this dated PC-SPAN scan-risk calculation.</p>
      </div>
      <form onSubmit={add} className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-6">
        <label className="grid gap-1 text-xs">
          <span className="font-medium">Instrument</span>
          <select value={instrument} onChange={(event) => setInstrument(event.target.value)} className="rounded border border-border bg-bg px-2 py-1.5 text-sm">
            <option value="">Choose an instrument</option>
            {MARGIN_LAB_INSTRUMENT_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.instruments.map((value) => <option key={value} value={value}>{value}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        <LabelInput label="Period" value={period} onChange={setPeriod} placeholder="e.g. Sep-26" />
        <label className="grid gap-1 text-xs">
          <span className="font-medium">Side</span>
          <select value={side} onChange={(event) => setSide(event.target.value as "B" | "S")} className="rounded border border-border bg-bg px-2 py-1.5 text-sm">
            <option value="B">Buy / long</option>
            <option value="S">Sell / short</option>
          </select>
        </label>
        <LabelInput label="Lots" value={quantity} onChange={setQuantity} inputMode="numeric" />
        <LabelInput label="Book (optional)" value={book} onChange={setBook} />
        <LabelInput label="Trader" value={trader} onChange={setTrader} />
        <div className="sm:col-span-2 lg:col-span-6">
          <button type="submit" className="ops-primary rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90">Add simulated position</button>
          {error && <span className="ml-2 text-xs text-negRed">{error}</span>}
        </div>
      </form>
      {positions.length > 0 && (
        <div className="space-y-1 text-xs">
          {positions.map((position) => (
            <div key={position.id} className="flex flex-wrap items-center gap-x-2 rounded border border-border/40 px-2 py-1.5">
              <span className="font-medium">{position.product} · {position.period}</span>
              <span className={cn("tabular-nums", position.signedQty < 0 && "text-negRed")}>{signed(position.signedQty)} lots</span>
              <span className="text-muted">{position.book ?? "Unbooked"} · {position.trader}</span>
              <button type="button" onClick={() => onChange(positions.filter((item) => item.id !== position.id))} className="ml-auto text-muted underline underline-offset-2 hover:text-negRed">Remove</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function LabelInput({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputMode?: "numeric";
}) {
  return (
    <label className="grid gap-1 text-xs">
      <span className="font-medium">{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} inputMode={inputMode} className="rounded border border-border bg-bg px-2 py-1.5 text-sm" />
    </label>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="ops-lab-metric rounded border border-border/40 p-2">
      <div className="text-muted">{label}</div>
      <div className="ops-lab-metric-value mt-0.5 font-semibold tabular-nums text-fg">{value}</div>
    </div>
  );
}

function ScopeSummary({ title, rows }: { title: string; rows: Array<{ label: string; count: number; netMw: number; grossMw: number }> }) {
  return (
    <div className="rounded border border-border/40 p-2">
      <div className="mb-1 font-medium text-fg">{title}</div>
      {rows.length === 0 ? <div className="text-muted">No selected or simulated positions.</div> : rows.map((row) => (
        <div key={row.label} className="flex justify-between gap-3 py-0.5 text-muted">
          <span className="min-w-0 truncate">{row.label} · {row.count}</span>
          <span className="shrink-0 tabular-nums">{signed(row.netMw)} / {number.format(row.grossMw)} lots</span>
        </div>
      ))}
    </div>
  );
}

function groupCandidates(
  positions: EexMarginLabCandidatePosition[],
  value: (position: EexMarginLabCandidatePosition) => string
): Array<{ label: string; positions: EexMarginLabCandidatePosition[] }> {
  const groups = new Map<string, EexMarginLabCandidatePosition[]>();
  for (const position of positions) {
    const label = value(position);
    const group = groups.get(label) ?? [];
    group.push(position);
    groups.set(label, group);
  }
  return [...groups.entries()]
    .map(([label, grouped]) => ({ label, positions: grouped }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function summarizeBy(
  positions: PositionLike[],
  value: (position: PositionLike) => string
): Array<{ label: string; count: number; netMw: number; grossMw: number }> {
  const summary = new Map<string, { count: number; netMw: number; grossMw: number }>();
  for (const position of positions) {
    const label = value(position);
    const current = summary.get(label) ?? { count: 0, netMw: 0, grossMw: 0 };
    current.count += 1;
    current.netMw += position.signedQty;
    current.grossMw += Math.abs(position.signedQty);
    summary.set(label, current);
  }
  return [...summary.entries()]
    .map(([label, values]) => ({ label, ...values }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function signed(value: number): string {
  return `${value > 0 ? "+" : ""}${number.format(value)}`;
}
